// Per-structure overlays from sim values: hemo segment series, dynamics parcel activity and pharma
// parcel engagement (as is, and by DKT region through the HD cortex's crosswalk), keyed by
// asset-pack structure id. The viewport colours keyed structures by value at the current time (the
// mock does too); unknown keys are skipped and reported.

import type { Colormap } from '../../viewport/api.ts';
import { colormap, type RampName } from '../../ui/scales.ts';
import { remap, type Crosswalk } from './crosswalk.ts';
import type { DynamicsSim } from './dynamics.ts';
import type { HemoSim } from './hemo.ts';
import { PharmaSim } from './pharma.ts';
import { frameTimes, type TimeAxis } from './time.ts';

export interface Overlay {
  id: string;
  /** What it shows, for legends and the inspector. */
  quantity: string;
  units: string;
  ramp: RampName;
  keys: string[];
  times: number[];
  /** `[frame][key]`; NaN keeps a structure's base colour. */
  values: Float32Array;
  axis: TimeAxis;
  colormap: Colormap;
  range: [number, number];
  /** How its values come from the sim's, when they aren't the sim's own keys (Methods says it). */
  method?: string;
  /** Pharma: the receptor target its engagement is of. */
  target?: string;
  /** Pharma at another dose (the Dose slider): each frame's factor over the run's own engagement, the
   *  target's occupancy ratio. The viewport scales its own course by it (`layersFor`). */
  doseRatio?: Float32Array;
}

/** The q-quantile of the finite values (q in 0–1). */
export function quantile(values: ArrayLike<number>, q: number): number {
  const v = Array.from(values).filter(Number.isFinite).sort((a, b) => a - b);
  if (!v.length) return NaN;
  const x = Math.min(v.length - 1, Math.max(0, q * (v.length - 1)));
  const i = Math.floor(x);
  return v[i]! + ((v[Math.min(v.length - 1, i + 1)] ?? v[i]!) - v[i]!) * (x - i);
}

/** A display range that ignores outliers: [lo, p98] (lo 0 for magnitudes, else p2). */
export function robustRange(values: ArrayLike<number>, magnitude: boolean): [number, number] {
  const lo = magnitude ? 0 : quantile(values, 0.02);
  let hi = quantile(values, 0.98);
  if (!Number.isFinite(hi) || hi <= lo) hi = lo + 1;
  return [Number.isFinite(lo) ? lo : 0, hi];
}

const HEMO_RAMP: Record<string, RampName> = { velocity: 'velocity', flow: 'velocity', pressure: 'pressure', area: 'perfusion' };

/** Segment values aggregated per structure. Speed and flow colour by magnitude; pressure as signed. */
export async function hemoOverlay(h: HemoSim, quantity: string, id = `hemo:${quantity}`): Promise<Overlay | null> {
  if (!h.isNetwork || !h.quantities.includes(quantity)) return null;
  const data = await h.series(quantity);
  const magnitude = quantity !== 'pressure';
  const { keys, values } = h.structureSeries(data, { abs: magnitude });
  if (!keys.length) return null;
  const ramp = HEMO_RAMP[quantity] ?? 'velocity';
  return { id, quantity, units: h.unit(quantity), ramp, keys, times: Array.from(frameTimes(h.axis)), values, axis: h.axis, colormap: colormap(ramp), range: robustRange(values, magnitude) };
}

/** Region activity on the structures its parcels name (`parcels[].structure`), on the slot's ramp if its scene names one. */
export async function dynamicsOverlay(d: DynamicsSim, id = 'dynamics:activity', ramp: RampName = 'activity'): Promise<Overlay | null> {
  if (!d.has('parcel_activity')) return null;
  const { keys, values, axis } = await d.structureSeries();
  if (!keys.length) return null;
  const range = d.activityRange ?? robustRange(values, true);
  return { id, quantity: 'activity', units: d.header.units?.activity ?? '', ramp, keys, times: Array.from(frameTimes(axis)), values, axis, colormap: colormap(ramp), range };
}

/**
 * A target's engagement per parcel over the run, on the fixed 0–1 scale design's Drugs screen paints and
 * the engine draws (viewport-api-v0, the engagement tint change): the colour comes from the engagement
 * itself (occupancy × relative density), and the tint's strength from where it sits on the scale, full
 * from 0.30. Under 0.15 design asks for a faint wash (tokens 0.1.4 `weak_mix`), which is the engine's to
 * draw; haloperidol's D2 (0.099 at most) is such a run. Another dose keeps the scale, so a lower one is
 * fainter.
 */
export async function pharmaOverlay(p: PharmaSim, target: string, id = 'pharma:engagement'): Promise<Overlay | null> {
  const m = await p.map('parcel', 'central', 'engagement');
  if (!m) return null;
  const keys = p.structureIds.length ? p.structureIds : Array.from({ length: m.locations }, (_, i) => String(i));
  const values = new Float32Array(m.frames * keys.length);
  for (let f = 0; f < m.frames; f++) values.set(PharmaSim.column(m, target, f), f * keys.length);
  // A target the map doesn't cover is all NaN: no overlay, rather than one that colours nothing.
  if (!values.some(Number.isFinite)) return null;
  const axis = m.frames > 1 ? p.axis : { ...p.axis, frames: 1 };
  return { id, quantity: 'engagement', units: m.desc.units ?? p.header.units?.engagement ?? '', ramp: 'occupancy', keys, times: Array.from(frameTimes(axis)).slice(0, m.frames), values, axis, colormap: colormap('occupancy'), range: [0, 1], target };
}

/**
 * A Schaefer-400 label's column in a pharma run: `structure_ids[label - 1]`, the numbering pharma's
 * parcel maps and an HD cortex's `schaefer400` layer share, checked against the id's own number.
 */
export function schaeferColumn(ids: string[]): (label: number) => number {
  return (label) => (/^ba:schaefer2018-400-\d+-(\d{3})$/.exec(ids[label - 1] ?? '')?.[1] === String(label).padStart(3, '0') ? label - 1 : -1);
}

/**
 * A parcel overlay as the crosswalk's structures (v0's DKT parcels, which the HD cortex's vertices
 * belong to): each region takes the mean over its vertices of the parcel value under each one.
 * Null when no region has a value.
 */
export function regionOverlay(o: Overlay, cw: Crosswalk, column: (label: number) => number, id: string): Overlay | null {
  const values = remap(o.values, o.times.length, o.keys.length, cw, column);
  if (!values.some(Number.isFinite)) return null;
  const method = `On the cortex by ${cw.to.toUpperCase()} region: each region is the mean over its vertices of the ${o.keys.length}-parcel map, from ${cw.pack.id} v${cw.pack.version}'s ${cw.from} and ${cw.to} layers (${cw.vertices.toLocaleString('en-US')} vertices).`;
  return { ...o, id, keys: cw.keys, values, method };
}

/** The `overlay` command for the viewport. */
export function overlayCommand(o: Pick<Overlay, 'id' | 'keys' | 'times' | 'values' | 'colormap' | 'range'> & { opacity?: number }) {
  return { cmd: 'overlay' as const, id: o.id, keys: o.keys, times: o.times, values: o.values, colormap: o.colormap, range: o.range, ...(o.opacity !== undefined ? { opacity: o.opacity } : {}) };
}
